// Quem usa a conta (E9). O titular convida o escritório e o contador por
// e-mail, avisa a pessoa pelo WhatsApp, troca o papel e tira o acesso; os
// outros veem quem usa e podem sair da empresa. Quem foi convidado entra com
// o e-mail do convite e aceita (ou recusa). Tudo só com internet: passa por
// funções do servidor, que conferem quem é titular e o e-mail do convite.
import { convites as convitesDados, usuarios } from '../dados.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { dataDoMomento, plural } from '../lib/formato.js';
import { PAPEIS, mensagemDoConvite as mensagemDoConviteDaLib, rotuloPapel } from '../lib/papeis.js';
import { emailValido } from '../lib/validacao.js';
import { ir, nestaTela } from '../rotas.js';
import { carregarConvites, carregarVinculos, ehTitular, escolherEmpresa, sair, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { confirmarSaida } from '../ui/sinal.js';
import { avisar, campo, carregando, confirmar, dialogo, faixa, formulario, secao, vazio } from '../ui/componentes.js';
import { destinoInicial, marca } from './entrada.js';
import { avisoDosTermos } from './legal.js';

const DIAS_DO_CONVITE = 30;

// Endereço do app (o mesmo em que o titular está), para a pessoa abrir.
function enderecoDoApp() {
  return `${location.origin}${location.pathname}`;
}

const mensagemDoConvite = (dados) => mensagemDoConviteDaLib({ ...dados, endereco: enderecoDoApp() });

const linkDoWhatsApp = (texto) => `https://wa.me/?text=${encodeURIComponent(texto)}`;

// Depois de convidar: a mensagem pronta para mandar pelo WhatsApp (o toque no
// botão abre o WhatsApp direto) ou copiar.
function avisarAPessoa({ email, papel, expira }) {
  const empresa = sessao.empresa.nome_fantasia || sessao.empresa.nome;
  const texto = mensagemDoConvite({ empresa, email, papel, expira });
  return dialogo((fechar) => [
    h('h2', null, 'Convite feito'),
    h('p', null, 'Agora avise ', h('strong', null, email), '. Mande a mensagem abaixo pelo WhatsApp ou copie e mande por onde quiser.'),
    h('pre', { class: 'mensagem-pronta' }, texto),
    h('div', { class: 'acoes' },
      h('a', { class: 'botao cheio', href: linkDoWhatsApp(texto), target: '_blank', rel: 'noopener', onClick: () => setTimeout(() => fechar(), 300) }, 'Mandar pelo WhatsApp'),
      h('button', {
        class: 'botao cheio secundario',
        type: 'button',
        onClick: async () => {
          try {
            await navigator.clipboard.writeText(texto);
            avisar('Mensagem copiada. Cole na conversa com a pessoa.');
          } catch {
            avisar('Não deu para copiar aqui. Selecione o texto e copie.', 'erro');
          }
        },
      }, 'Copiar a mensagem'),
      h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => fechar() }, 'Fechar')),
  ]);
}

const daquiA = (dias) => new Date(Date.now() + dias * 86400000).toISOString();
const vencido = (c) => new Date(c.expira_em).getTime() <= Date.now();

// ---------------------------------------------------------------------
// Usuários da conta
// ---------------------------------------------------------------------
export function telaUsuarios() {
  const tela = nestaTela();
  const titular = ehTitular();
  const corpo = h('div', { class: 'lista' }, carregando());

  const executar = async (acao, sucesso) => {
    try {
      await acao();
      if (sucesso) avisar(sucesso);
      tela.renderizar();
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
    }
  };

  const cartaoDoMembro = (m) => {
    const outro = titular && !m.eu && m.papel !== 'titular';
    const novoPapel = m.papel === 'contador' ? 'escritorio' : 'contador';
    return h('div', { class: 'cartao usuario' },
      h('div', { class: 'cartao-corpo' },
        h('span', { class: 'cartao-titulo' }, m.email, m.eu && h('small', null, ' (você)')),
        h('span', { class: 'cartao-linha' }, `${rotuloPapel(m.papel)}: ${PAPEIS[m.papel]?.faz ?? ''}`),
        h('span', { class: 'cartao-linha' }, `Desde ${dataDoMomento(m.desde)}`),
        outro && h('div', { class: 'acoes-linha' },
          h('button', {
            class: 'botao secundario',
            type: 'button',
            onClick: async () => {
              const ok = await confirmar({
                titulo: `Mudar para ${rotuloPapel(novoPapel).toLowerCase()}?`,
                texto: `${m.email} vai ficar como ${rotuloPapel(novoPapel).toLowerCase()}: ${PAPEIS[novoPapel].faz}.`,
                sim: 'Mudar',
              });
              if (ok) executar(() => usuarios.trocarPapel(m.user_id, novoPapel), `Agora é ${rotuloPapel(novoPapel).toLowerCase()}.`);
            },
          }, `Mudar para ${rotuloPapel(novoPapel).toLowerCase()}`),
          h('button', {
            class: 'botao perigo',
            type: 'button',
            onClick: async () => {
              const ok = await confirmar({
                titulo: 'Tirar o acesso?',
                texto: `${m.email} deixa de ver a conta, e os dados dela saem do aparelho dessa pessoa. O que ela já lançou e enviou continua na conta. Para voltar, é preciso convidar de novo.`,
                sim: 'Tirar o acesso',
                perigo: true,
              });
              if (ok) executar(() => usuarios.tirarAcesso(m.user_id), 'Acesso retirado.');
            },
          }, 'Tirar o acesso'))));
  };

  const cartaoDoConvite = (c) => {
    const venceu = vencido(c);
    const empresa = sessao.empresa.nome_fantasia || sessao.empresa.nome;
    return h('div', { class: 'cartao usuario' },
      h('div', { class: 'cartao-corpo' },
        h('span', { class: 'cartao-titulo' }, c.email),
        h('span', { class: 'cartao-linha' }, `${rotuloPapel(c.papel)} · convidado em ${dataDoMomento(c.enviado_em)}`),
        h('span', { class: `cartao-linha${venceu ? ' perigo' : ''}` },
          venceu ? `O convite venceu em ${dataDoMomento(c.expira_em)}.` : `Esperando a pessoa aceitar. Vale até ${dataDoMomento(c.expira_em)}.`),
        h('div', { class: 'acoes-linha' },
          venceu
            ? h('button', {
              class: 'botao secundario',
              type: 'button',
              onClick: () => executar(async () => {
                await usuarios.convidar(c.email, c.papel);
                await avisarAPessoa({ email: c.email, papel: c.papel, expira: daquiA(DIAS_DO_CONVITE) });
              }, null),
            }, 'Convidar de novo')
            : h('a', {
              class: 'botao secundario',
              href: linkDoWhatsApp(mensagemDoConvite({ empresa, email: c.email, papel: c.papel, expira: c.expira_em })),
              target: '_blank',
              rel: 'noopener',
            }, 'Mandar pelo WhatsApp'),
          h('button', {
            class: 'botao perigo',
            type: 'button',
            onClick: async () => {
              const ok = await confirmar({ titulo: 'Cancelar o convite?', texto: `${c.email} não vai mais conseguir aceitar este convite.`, sim: 'Cancelar o convite', nao: 'Voltar', perigo: true });
              if (ok) executar(() => usuarios.cancelarConvite(c.id), 'Convite cancelado.');
            },
          }, 'Cancelar convite'))));
  };

  const formularioDeConvite = () => formulario({
    filhos: [
      campo({
        rotulo: 'E-mail da pessoa',
        nome: 'email',
        tipo: 'email',
        obrigatorio: true,
        dica: 'Ela vai entrar no HSA Frota com este e-mail: o código de entrada chega nele.',
        atributos: { autocomplete: 'off', inputmode: 'email', autocapitalize: 'none', spellcheck: 'false', placeholder: 'nome@gmail.com' },
      }),
      campo({
        rotulo: 'O que ela pode fazer',
        nome: 'papel',
        tipo: 'segmentos',
        valor: 'escritorio',
        opcoes: [['escritorio', PAPEIS.escritorio.nome, PAPEIS.escritorio.faz], ['contador', PAPEIS.contador.nome, PAPEIS.contador.faz]],
      }),
    ],
    textoBotao: 'Convidar',
    textoEnviando: 'Convidando…',
    validar: (d) => (emailValido(d.email) ? {} : { email: 'Digite um e-mail válido, como nome@gmail.com.' }),
    aoEnviar: async (d) => {
      const email = d.email.trim().toLowerCase();
      await usuarios.convidar(email, d.papel);
      await avisarAPessoa({ email, papel: d.papel, expira: daquiA(DIAS_DO_CONVITE) });
      tela.renderizar();
    },
  });

  (async () => {
    let lista;
    try {
      lista = await usuarios.listar();
    } catch (erro) {
      trocar(corpo,
        faixa(mensagemDeErro(erro), 'alerta'),
        h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => tela.renderizar() }, 'Tentar de novo'));
      return;
    }
    const ativos = lista.membros.filter((m) => m.ativo);
    const antigos = lista.membros.filter((m) => !m.ativo);
    trocar(corpo,
      secao('Quem usa esta conta', h('div', { class: 'lista' }, ativos.map(cartaoDoMembro)), { contador: String(ativos.length) }),
      titular && lista.convites.length > 0 && secao('Convites em aberto', h('div', { class: 'lista' }, lista.convites.map(cartaoDoConvite)),
        { contador: String(lista.convites.length) }),
      titular && secao('Convidar alguém', h('div', { class: 'lista' },
        h('p', { class: 'pequeno' }, 'Convide quem trabalha com você: o escritório lança, o contador só consulta. Não tem limite de pessoas.'),
        formularioDeConvite())),
      titular && antigos.length > 0 && secao('Já tiveram acesso', h('div', { class: 'lista' },
        antigos.map((m) => h('div', { class: 'cartao usuario' }, h('div', { class: 'cartao-corpo' },
          h('span', { class: 'cartao-titulo' }, m.email),
          h('span', { class: 'cartao-linha' }, `${rotuloPapel(m.papel)} · sem acesso desde ${dataDoMomento(m.saiu_em)}`)))))),
      !titular && secao('Sair desta empresa', h('div', { class: 'lista' },
        h('p', { class: 'pequeno' }, 'Você deixa de ver a conta, e os dados dela saem deste aparelho. Para voltar, o titular precisa convidar de novo.'),
        h('button', {
          class: 'botao perigo cheio',
          type: 'button',
          onClick: async () => {
            const ok = await confirmar({ titulo: 'Sair desta empresa?', texto: 'Você deixa de ver a conta. Para voltar, o titular precisa convidar de novo.', sim: 'Sair da empresa', perigo: true });
            if (!ok) return;
            try {
              await usuarios.sairDaEmpresa();
              await carregarVinculos();
              avisar('Você saiu da empresa.');
              ir(destinoInicial(), { substituir: true });
            } catch (erro) {
              avisar(mensagemDeErro(erro), 'erro');
            }
          },
        }, 'Sair desta empresa'))));
  })();

  return casca({
    titulo: 'Usuários',
    voltar: '/conta',
    conteudo: [
      h('p', { class: 'pequeno' }, titular
        ? 'Quem pode entrar nesta conta e o que cada um pode fazer. Precisa de internet.'
        : 'Quem pode entrar nesta conta. Só o titular convida e muda o que cada um pode fazer.'),
      corpo,
    ],
  });
}

// ---------------------------------------------------------------------
// Convites para quem está entrando
// ---------------------------------------------------------------------
export function telaConvites() {
  const tela = nestaTela();
  const primeira = sessao.vinculos.length === 0;
  const corpo = h('div', { class: 'lista' }, carregando());

  const aceitar = async (c, botao) => {
    botao.disabled = true;
    try {
      const empresaId = await convitesDados.aceitar(c.id);
      await carregarVinculos();
      escolherEmpresa(sessao.vinculos.find((v) => v.empresa.id === empresaId) ?? null);
      ir(sessao.empresa ? '/inicio?convite=1' : destinoInicial(), { substituir: true });
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
      if (botao.isConnected) botao.disabled = false;
    }
  };

  const recusar = async (c) => {
    const ok = await confirmar({ titulo: 'Recusar o convite?', texto: `Você não vai usar a conta de ${c.empresa}. Se mudar de ideia, peça um convite novo.`, sim: 'Recusar', nao: 'Voltar', perigo: true });
    if (!ok) return;
    try {
      await convitesDados.recusar(c.id);
      await carregarConvites();
      avisar('Convite recusado.');
      if (sessao.convites.length === 0) ir(destinoInicial(), { substituir: true });
      else tela.renderizar();
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
    }
  };

  (async () => {
    await carregarConvites();
    if (sessao.convites.length === 0) {
      trocar(corpo, vazio({
        simbolo: 'pessoas',
        titulo: 'Nenhum convite em aberto',
        texto: 'Se alguém convidou você, confira se entrou com o mesmo e-mail do convite. O convite vale 30 dias.',
      }));
      return;
    }
    trocar(corpo, sessao.convites.map((c) => {
      const botaoAceitar = h('button', { class: 'botao cheio', type: 'button' }, 'Aceitar o convite');
      botaoAceitar.addEventListener('click', () => aceitar(c, botaoAceitar));
      return h('div', { class: 'cartao convite' },
        h('div', { class: 'cartao-corpo' },
          h('span', { class: 'cartao-titulo' }, c.empresa),
          c.cidade && h('span', { class: 'cartao-linha' }, c.cidade),
          h('span', { class: 'cartao-linha' }, `Como ${rotuloPapel(c.papel).toLowerCase()}: ${PAPEIS[c.papel]?.faz ?? ''}.`),
          h('span', { class: 'cartao-linha' }, `Vale até ${dataDoMomento(c.expira_em)}.`),
          h('div', { class: 'acoes' },
            botaoAceitar,
            h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => recusar(c) }, 'Recusar')),
          avisoDosTermos('Aceitar o convite')));
    }));
  })().catch((erro) => trocar(corpo, faixa(mensagemDeErro(erro), 'alerta')));

  const conteudo = [
    h('p', { class: 'subtitulo' }, `Você entrou com ${sessao.usuario?.email ?? 'seu e-mail'}.`),
    corpo,
    h('a', { class: 'botao texto', href: '#/empresa/nova' }, primeira ? 'Não é isso: quero cadastrar a minha empresa' : 'Cadastrar outra empresa'),
  ];

  if (primeira) {
    return h('div', { class: 'entrada' },
      marca(),
      h('div', null, h('h1', null, sessao.convites.length === 1 ? 'Você recebeu um convite' : 'Convites para você')),
      conteudo,
      h('button', {
        class: 'botao texto',
        type: 'button',
        onClick: async () => {
          if (!(await confirmarSaida())) return;
          await sair();
          ir('/entrar');
        },
      }, 'Sair'));
  }
  return casca({ titulo: 'Convites', voltar: sessao.empresa ? '/inicio' : '/empresas', abas: false, conteudo });
}

// Para o Início: o cartão dos convites em aberto (quem já usa outra conta).
export function cartaoDosConvites() {
  if (!sessao.convites?.length) return null;
  const [primeiro] = sessao.convites;
  return h('a', { class: 'cartao convite-pendente', href: '#/convites' },
    h('span', { class: 'cartao-corpo' },
      h('span', { class: 'cartao-titulo' }, sessao.convites.length === 1 ? 'Convite para você' : `${plural(sessao.convites.length, 'convite')} para você`),
      h('span', { class: 'cartao-linha' }, `${primeiro.empresa} convidou você como ${rotuloPapel(primeiro.papel).toLowerCase()}.`)));
}
